import { BBox, bbox, featureCollection, Geometry, point } from '@turf/turf';
import { MapApi, MapInfo } from 'api/MapApi';
import { Flex, Spacer } from 'components/UI/FormUI';
import useMap from 'hooks/useMap';
import debounce from 'lodash/debounce';
import { DateRangeDto } from 'model/TimeDto';
import React, { useEffect, useMemo, useRef, useState, FC } from 'react';
import { useMenu } from 'reducers/menuReducer';
import styled from 'styled-components';
import { Box, Button, Label, Text, tombac, TooltipOnHover } from 'tombac';
import {
  BackIcon,
  ChevronDownIcon,
  ChevronSLeftIcon,
  ChevronSRightIcon,
  ChevronUpIcon,
  ForwardIcon,
  HelpIcon,
  InfoIcon as TombacInfoIcon,
  MapIcon,
  StarIcon,
} from 'tombac-icons';
import { MapsLoading } from './MapsLoading';
import './MultipleMapSelector.css';

interface MapVersionSliderProps { 
  disabled?: boolean;
  children?: React.ReactNode;
}

const SelectorSideArrow = styled.div`
  width: 24px;
  opacity: 1;
  transition-duration: 0.1s;
  ${Button} {
    width: 24px;
    min-width: 24px;
  }
`;

export const SelectorBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 455px;
  :hover ${SelectorSideArrow} {
    opacity: 1;
  }
`;

const ArrowButton = styled(Button)`
  &.disabled {
    cursor: not-allowed;
  }
`;

const MapVersionLabelComponent = styled.div`
  font-family: ${tombac.fontFamily};
  font-size: 12px;
  color: #7a7e80;
  width: 70px;
  text-align: center;
  line-height: 20px;
  &.disabled {
    filter: grayscale(100%);
    cursor: not-allowed;
  }
`;

const MapVersionCircleBox = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 4px solid #e5e5e5;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &.disabled {
    filter: grayscale(100%);
    cursor: not-allowed;
  }
  svg {
    color: #fff;
    transform: scale(0.7);
  }
`;

const SuggestedLineComponent = styled.div`
  height: 6px;
  background-color: #bcdcf5;
  width: 100%;
  position: absolute;
  top: 50%;
  margin-top: 7px;
  z-index: -1;
  &.disabled {
    filter: grayscale(100%);
    cursor: not-allowed;
  }
`;

const SuggestedLine: FC<MapVersionSliderProps> = ({ disabled, children }) => (
  <SuggestedLineComponent className={disabled ? 'disabled' : undefined}>
    {children}
  </SuggestedLineComponent>
);

const MapVersionLabel: FC<MapVersionSliderProps> = ({ disabled, children }) => (
  <MapVersionLabelComponent className={disabled ? 'disabled' : undefined}>
    {children}
  </MapVersionLabelComponent>
);

const MapVersionCircle = ({ suggested, disabled }: { suggested: boolean; disabled?: boolean }) => (
  <MapVersionCircleBox className={disabled ? 'disabled' : undefined}>
    {suggested ? <StarIcon size="sm" /> : null}
  </MapVersionCircleBox>
);

const MapVersions = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const MapVersionsLineComponent = styled.div`
  height: 6px;
  background-color: #e5e5e5;
  width: 100%;
  position: absolute;
  margin-top: 24px;
  top: 0;
  z-index: -1;
  &.disabled {
    filter: grayscale(100%);
    cursor: not-allowed;
  }
`;

const MapVersionsLine: FC<MapVersionSliderProps> = ({ disabled, children }) => (
  <MapVersionsLineComponent className={disabled ? 'disabled' : undefined}>
    {children}
  </MapVersionsLineComponent>
);

const MapVersionButton = styled.div<{ active?: boolean; suggested?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 12px;
  cursor: pointer;
  position: relative;
  ${MapVersionLabel} {
    color: ${(p) => (p.active ? '#2a90d5' : '#7a7e80')};
    font-weight: ${(p) => (p.active ? '500' : 'normal')};
  }
  ${MapVersionCircleBox} {
    transform: ${(p) => (p.suggested ? 'scale(1.2)' : '')};
    transform: ${(p) => (p.active ? 'scale(1.15)' : '')};
    background: ${(p) => (p.suggested ? '#bcdcf5' : '')};
    background: ${(p) => (p.active ? '#2a90d5' : '')};
    border: ${(p) => (p.active || p.suggested ? 'none' : '')};
  }
`;

export const SelectorHeader = styled.div`
  display: flex;
  align-items: center;
  height: 51px;
  padding: 0 16px;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
`;

const SuggestedLabel = styled.div`
  font-family: ${tombac.fontFamily};
  font-size: 12px;
  color: ${tombac.color('neutral', 700)};
  display: flex;
  align-items: center;
`;

const SelectorContent = styled.div`
  padding: 10px 8px 32px 8px;
`;

const SelectedMapBadge = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  margin-right: 8px;
  border-radius: 3px;
  background-color: ${tombac.color('accent', 200)};
  font-family: ${tombac.fontFamily};
  font-size: 14px;
  color: ${tombac.color('accent', 600)};
`;

export const shouldRecommendMapVersion = (
  maps: MapInfo[],
  dateRanges: DateRangeDto[],
) => {
  if (!maps || maps.length === 0) {
    return () => false;
  }

  const datesSorted = dateRanges
    .flatMap((it) => [new Date(it.startDate), new Date(it.endDate)])
    .sort((a, b) => a.getTime() - b.getTime());

  const minDate = datesSorted[0];
  const maxDate = datesSorted[datesSorted.length - 1];

  if (minDate === undefined || maxDate === undefined) {
    return () => false;
  }

  let firstMap: MapInfo = [...maps]
    .map((it) => ({
      distance:
        it.strict && it.releaseTimestamp > maxDate.getTime()
          ? Infinity
          : Math.abs(it.releaseTimestamp - minDate.getTime()),
      map: it,
    }))
    .sort((a, b) => a.distance - b.distance)[0].map;

  let lastMap: MapInfo = [...maps]
    .map((it) => ({
      distance:
        it.strict && it.releaseTimestamp > maxDate.getTime()
          ? Infinity
          : Math.abs(it.releaseTimestamp - maxDate.getTime()),
      map: it,
    }))
    .sort((a, b) => a.distance - b.distance)[0].map;

  return (mapInfo: MapInfo): boolean => {
    if (!firstMap && !lastMap) {
      return false;
    }
    if (!firstMap) {
      return mapInfo.releaseTimestamp <= lastMap.releaseTimestamp;
    }
    if (!lastMap) {
      return mapInfo.releaseTimestamp >= firstMap.releaseTimestamp;
    }
    return (
      mapInfo.releaseTimestamp >= firstMap.releaseTimestamp &&
      mapInfo.releaseTimestamp <= lastMap.releaseTimestamp
    );
  };
};

interface MultipleMapSelectorProps {
  geometry?: Geometry;
  disabled?: boolean;
  disabledReason?: string;
}

const MultipleMapSelector: FC<MultipleMapSelectorProps> = ({ geometry, disabled, disabledReason }) => {
  const [menu, setMenu] = useMenu();
  const map = useMap();
  const [maps, setMaps] = useState<MapInfo[]>([]);
  const [mapsLoading, setMapsLoading] = useState<'idle' | 'loading' | 'ready'>(
    'idle',
  );
  const loadedMapsBbox = useRef<BBox>();
  const [bounds, setBounds] = useState<BBox | undefined>();
  const [pageCursor, setPageCursor] = useState(0);
  const [hidden, setHidden] = useState(true);

  const setMapVersion = (map: MapInfo) => {
    setMenu({ mapVersion: map.version, mapType: map.type });
  };

  // Update bounds on map movement
  useEffect(() => {
    const handleMove = debounce(() => {
      const bounds = map.getBounds();
      const [a, b] = bounds.toArray();
      const mapQueryBbox = bbox(featureCollection([point(a), point(b)]));

      setBounds((it) => {
        if (JSON.stringify(it) !== JSON.stringify(mapQueryBbox)) {
          return mapQueryBbox;
        }
        return it;
      });
    }, 1000);

    map.on('moveend', handleMove);

    handleMove();

    return () => {
      map.off('moveend', handleMove);
    };
  }, [map]);

  // Fetch new versions
  useEffect(() => {
    if (map.getZoom() < 8 && !geometry) {
      return;
    }

    const mapQueryBbox = geometry ? bbox(geometry) : bounds;

    if (loadedMapsBbox.current === mapQueryBbox) {
      return;
    }

    let current = true;
    setMapsLoading('loading');

    if (mapQueryBbox === undefined) {
      return;
    }

    MapApi.getMaps(mapQueryBbox).then((maps) => {
      if (!current) return;

      const newMaps = maps.filter(
        (it) => it.type === 'DSEG_NOSPLIT' || it.type === 'OPEN_DSEG_NOSPLIT',
      );
      setMaps((it) => {
        if (JSON.stringify(it) !== JSON.stringify(newMaps)) {
          return newMaps;
        }
        return it;
      });
      if (
        newMaps.length !== 0 &&
        !newMaps.some((it) => it.version === menu.mapVersion)
      ) {
        const map =
          [...newMaps]
            .sort((b, a) => b.releaseTimestamp - a.releaseTimestamp)
            .find(shouldRecommend) ?? newMaps[newMaps.length - 1];
        setMapVersion(map);
        setPageCursor(
          Math.min(
            newMaps.findIndex((it) => it.version === map.version),
            newMaps.length - pageWidth,
          ),
        );
      }
      setMapsLoading('ready');
      loadedMapsBbox.current = mapQueryBbox;
    });

    return () => {
      current = false;
    };
  }, [geometry, bounds]);

  const shouldRecommend = useMemo(
    () => shouldRecommendMapVersion(maps, menu.dateRanges),
    [maps, menu.dateRanges],
  );

  const pageWidth = 4;

  // Set version to recommended on inital render
  const switched = useRef(false);
  useEffect(() => {
    if (switched.current) {
      return;
    }
    if (
      menu.mapVersion !== undefined &&
      menu.mapVersion !== '' &&
      maps.length
    ) {
      if (!maps.some((map) => map.version === menu.mapVersion)) {
        const selectedMaps = maps.filter((map) =>
          map.version.startsWith(menu.mapVersion),
        );

        const map =
          [...selectedMaps]
            .sort((b, a) => b.releaseTimestamp - a.releaseTimestamp)
            .find(shouldRecommend) ?? selectedMaps[selectedMaps.length - 1];

        map && setMapVersion(map);
      }
      setPageCursor(
        Math.min(
          maps.findIndex((it) => it.version === menu.mapVersion),
          maps.length - pageWidth,
        ),
      );
      switched.current = true;
      return;
    }

    if (
      (menu.mapVersion === undefined || menu.mapVersion === '') &&
      maps.length > 0
    ) {
      const map =
        [...maps]
          .sort((a, b) => b.releaseTimestamp - a.releaseTimestamp)
          .find(shouldRecommend) ?? maps[maps.length - 1];
      setMapVersion(map);
      setPageCursor(
        Math.min(
          maps.findIndex((it) => it.releaseTimestamp === map.releaseTimestamp),
          maps.length - pageWidth,
        ),
      );
      switched.current = true;
    }
  }, [maps, menu.mapVersion]);

  if (mapsLoading === 'ready' && maps.length === 0) {
    return (
      <>
        <SelectorBox>
          <SelectorHeader>No maps available in this area</SelectorHeader>
        </SelectorBox>
      </>
    );
  }

  if ((mapsLoading === 'loading' && maps.length === 0) || !menu.mapVersion) {
    return (
      <>
        <SelectorBox>
          <SelectorHeader>
            {map.getZoom() < 8 && (
              <>
                <TombacInfoIcon width="14px" />
                <Text size="s" $ml="1sp">
                  Zoom in to see map versions
                </Text>
              </>
            )}
            {map.getZoom() >= 8 && <MapsLoading />}
          </SelectorHeader>
        </SelectorBox>
      </>
    );
  }

  const mapsToShow = maps.slice(
    Math.max(0, pageCursor),
    Math.min(maps.length, pageCursor + pageWidth),
  );

  const canSwitchMap = (direction?: 'next' | 'prev') => {
    if (disabled) {
      return false;
    }
    if (!direction) {
      return true;
    }
    const diff = direction === 'next' ? 1 : -1;

    const mapIndex = maps.findIndex(
      (it) => menu.mapVersion === it.version && menu.mapType === it.type,
    );
    const mapsLength = maps.length;
    const newIndex = mapIndex + diff;
    const canMove = newIndex >= 0 && newIndex < mapsLength;

    return canMove;
  };

  const switchMap = (direction: 'next' | 'prev') => {
    if (!canSwitchMap(direction)) {
      return;
    }
    const diff = direction === 'next' ? 1 : -1;

    const mapIndex = maps.findIndex(
      (it) => menu.mapVersion === it.version && menu.mapType === it.type,
    );
    const mapsLength = maps.length;
    const newIndex = mapIndex + diff;
    const canMove = newIndex >= 0 && newIndex < mapsLength;

    if (!canMove) {
      return;
    }

    let newCursor = pageCursor;

    const shouldMovePageCursor =
      newIndex < Math.max(0, pageCursor) ||
      newIndex >= Math.min(mapsLength, pageCursor + pageWidth);

    if (shouldMovePageCursor) {
      const isAhead = newIndex >= pageCursor + pageWidth;
      newCursor = isAhead ? newIndex - pageWidth + 1 : newIndex;
    }

    setMapVersion(maps[newIndex]);
    setPageCursor(newCursor);
  };

  return (
    <>
      <SelectorBox>
        <SelectorHeader onClick={() => setHidden((it) => !it)}>
          <MapIcon />
          <Label $ml="1sp" $mr="1sp">
            Map version
          </Label>
          <TooltipOnHover
            variant="default"
            placement="top"
            content={
              <Text $width="300u" fontSize={14} lineHeight={1.4}>
                Selecting the best map version for your analysis can help you
                with better map matching of TomTom data to the road network.
                Older map enables you to analyze traffic information based on
                older road geometries. Newer map allows you to perform analysis
                on the up-to-date road network.
              </Text>
            }
          >
            <HelpIcon />
          </TooltipOnHover>
          <Spacer />
          <SelectedMapBadge>
            Selected map:
            <b style={{ marginLeft: '4px' }}>{menu.mapVersion}</b>
          </SelectedMapBadge>
          {hidden ? (
            <ChevronUpIcon />
          ) : (
            <ChevronDownIcon />
          )}
        </SelectorHeader>
        <SelectorContent hidden={hidden} title={disabledReason}>
          <Flex $alignItems="center" $marginLeft="24u" $marginRight="24u">
            <div style={{ marginRight: 'auto' }}>
              <Label $mr="1sp">Change map</Label>
              <ArrowButton
                disabled={!canSwitchMap('prev')}
                onClick={() => switchMap('prev')}
                shape="circle"
                size="xs"
                className={!canSwitchMap('prev') ? "disabled" : undefined}
              >
                <BackIcon />
              </ArrowButton>
              <ArrowButton
                $ml="0.5sp"
                disabled={!canSwitchMap('next')}
                shape="circle"
                size="xs"
                onClick={() => switchMap('next')}
                className={!canSwitchMap('next') ? "disabled" : undefined}
              >
                <ForwardIcon />
              </ArrowButton>
            </div>

            <SuggestedLabel>
              <MapVersionCircleBox
                style={{
                  background: '#bcdcf5',
                  display: 'inline-flex',
                  border: 'none',
                  marginRight: '5px',
                  filter: disabled ? 'grayscale(100%)' : undefined,
                }}
              >
                <StarIcon size="sm" />
              </MapVersionCircleBox>
              Suggested map versions
            </SuggestedLabel>
          </Flex>

          <Flex alignItems="center" $mt="2sp">
            <SelectorSideArrow>
              {pageCursor > 0 && (
                <Button
                  onClick={() => setPageCursor((it) => it - 1)}
                  shape="circle"
                  size="xs"
                  variant="flat"
                >
                  <ChevronSLeftIcon style={{ fontSize: '1rem' }} />
                </Button>
              )}
            </SelectorSideArrow>
            <MapVersions>
              <MapVersionsLine disabled={disabled} />
              {mapsToShow
                .sort((a, b) => a.releaseTimestamp - b.releaseTimestamp)
                .map((it) => (
                  <MapVersionButton
                    key={it.version + it.releaseTimestamp + it.type}
                    active={it.version === menu.mapVersion && it.type === menu.mapType}
                    onClick={!disabled ? () => setMapVersion(it) : undefined}
                    suggested={shouldRecommend(it)}
                  >
                    <MapVersionLabel disabled={disabled}>{it.version}</MapVersionLabel>
                    <MapVersionCircle suggested={shouldRecommend(it)} disabled={disabled} />
                    {shouldRecommend(it) && <SuggestedLine disabled={disabled} />}
                  </MapVersionButton>
                ))}
            </MapVersions>
            {mapsToShow.length === 2 && mapsToShow[0].version === '2016.12' && (
              <>
                <Spacer width={2} />
                <TooltipOnHover
                  variant="inverted"
                  placement="top"
                  content={
                    <>
                      Only 2 map versions are available, when <br />
                      selected date range is older than 2 years
                    </>
                  }
                >
                  <TombacInfoIcon color="#F9B022" />
                </TooltipOnHover>
                <Box>Why do you see only 2 maps?</Box>
              </>
            )}
            <SelectorSideArrow>
              {pageCursor < maps.length - pageWidth && (
                <Button
                  onClick={() => setPageCursor((it) => it + 1)}
                  shape="circle"
                  size="xs"
                  variant="flat"
                >
                  <ChevronSRightIcon style={{ fontSize: '1rem' }} />
                </Button>
              )}
            </SelectorSideArrow>
          </Flex>
        </SelectorContent>
      </SelectorBox>
    </>
  );
}

export default MultipleMapSelector;
